'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Store } from '../types/models/store';
import { useAuth } from './AuthContext';
import { storeAPI, employeeAssignmentAPI } from '../api';

interface StoreContextType {
  selectedStore: Store | null;
  setSelectedStore: (store: Store | null) => void;
  availableStores: Store[];
  setAvailableStores: (stores: Store[]) => void;
  refreshStores: () => Promise<void>;  // 店舗作成/更新/削除後に一覧を再取得
  isLoading: boolean;
  isStoreReady: boolean;  // 店舗コンテキストが準備完了かどうか
  error: string | null;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

interface StoreProviderProps {
  children: ReactNode;
}

export const StoreProvider: React.FC<StoreProviderProps> = ({ children }) => {
  const [selectedStore, _setSelectedStore] = useState<Store | null>(null);
  const [availableStores, setAvailableStores] = useState<Store[]>([]);
  const [isLoading, setIsLoading] = useState(true);  // 初期値 true（初期化完了までローディング）
  const [isStoreReady, setIsStoreReady] = useState(false);  // 店舗コンテキスト準備完了フラグ
  const [error, setError] = useState<string | null>(null);
  const { employee, user } = useAuth();

  // 店舗データの取得と従業員の所属店舗を自動設定
  const fetchStores = useCallback(async () => {
      try {
        setIsLoading(true);
        setIsStoreReady(false);  // 初期化中は false
        setError(null);
        const isAdmin = (employee as any)?.role_type === 'ADMIN';  // role_type のみで判定（is_admin は使わない）
        // ADMIN は全店対象。バックエンドは include_unassigned を付けないと「配属店舗のみ」を
        // 返すため、これが無いと自分未配属のオーナー（新規会社の初回店舗など）に店舗が出ない。
        const response = await storeAPI.getStores(isAdmin ? { include_unassigned: 1 } : undefined);
        if (response.success) {
          // 権限に応じて店舗候補を制限
          let stores = response.data as Store[];
          if (!isAdmin) {
            try {
              // user_id を優先的に使用（employee が null でも user があれば取得可能）
              const uid = (user as any)?.id || (employee as any)?.user_id;
              if (uid) {
                const assignRes = await employeeAssignmentAPI.getCurrentAssignments(uid);
                const activeAssignments = (assignRes?.data || []) as any[];
                const assignedStoreIds = activeAssignments
                  .filter(a => a.assignment_type === 'store' && !!a.store_id)
                  .map(a => Number(a.store_id));
                stores = assignedStoreIds.length > 0 ? stores.filter(s => assignedStoreIds.includes(Number(s.id))) : [];
              } else {
                // user_id も employee.user_id もない場合は空配列
                stores = [];
              }
            } catch (e) {
              // フォールバックなし（employees.store_id は廃止）
              console.warn('Failed to fetch employee assignments:', e);
              stores = [];
            }
          }

          setAvailableStores(stores);

          // 1) ローカルストレージに保存された店舗があれば最優先で適用
          let selected: Store | null = null;
          if (typeof window !== 'undefined') {
            const savedId = window.localStorage.getItem('selected_store_id');
            if (savedId) {
              const savedStore = stores.find(store => String(store.id) === String(savedId));
              if (savedStore) {
                selected = savedStore;
              } else {
                // フィルタ後に選択不可なら保存値を消す
                window.localStorage.removeItem('selected_store_id');
              }
            }
          }

          // 2) localStorage に無い場合は先頭を自動選択
          if (!selected && stores.length > 0) {
            selected = stores[0];
          }

          // 店が 0 件なら選択を空にする（2026-10-17・設計書 §9.4。以前は前の店が残り、その店の ID で要求が出続けた）
          if (stores.length === 0) {
            _setSelectedStore(null);
            if (typeof window !== 'undefined') {
              try { window.localStorage.removeItem('selected_store_id'); } catch {}
              (window as any).__logmeSelectedStoreId = undefined;
            }
          }

          // 3) 選択を確定し localStorage に保存
          if (selected) {
            _setSelectedStore(selected);
            if (typeof window !== 'undefined') {
              window.localStorage.setItem('selected_store_id', String(selected.id));
              (window as any).__logmeSelectedStoreId = selected.id;
            }
          }
        }
      } catch (err) {
        setError('店舗データの取得に失敗しました');
        console.error('Failed to fetch stores:', err);
      } finally {
        setIsLoading(false);
        setIsStoreReady(true);  // 成功・失敗に関わらず準備完了
      }
  }, [employee, user]);

  useEffect(() => {
    // employee または user が存在する場合に店舗を取得
    if (employee || user) {
      fetchStores();
    }
  }, [employee, user, fetchStores]);

  // 永続化付きのsetter
  const setSelectedStore = (store: Store | null) => {
    _setSelectedStore(store);
    if (typeof window !== 'undefined') {
      try {
        if (store) {
          window.localStorage.setItem('selected_store_id', String(store.id));
          (window as any).__logmeSelectedStoreId = store.id;
        } else {
          window.localStorage.removeItem('selected_store_id');
          (window as any).__logmeSelectedStoreId = undefined;
        }
      } catch {}
    }
  };

  const value: StoreContextType = {
    selectedStore,
    setSelectedStore,
    availableStores,
    setAvailableStores,
    refreshStores: fetchStores,
    isLoading,
    isStoreReady,
    error,
  };

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = (): StoreContextType => {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
};
